/**
 * useApi / useApiMutation — minimal data-fetching hooks that wrap `apiClient`.
 *
 * The aim is a *boring*, predictable interface. No SWR-style cache, no
 * retry-with-backoff, no prefetch — those features add complexity that the
 * existing pages don't need. If a page later wants something fancier, it can
 * keep calling `apiClient` directly.
 *
 * Usage
 * ─────
 *
 *   // GET — runs once on mount, gives you `{ data, loading, error, refetch }`.
 *   const { data, loading, error, refetch } = useApi('/ultra-admin/data-health');
 *
 *   // POST / PUT / DELETE — gives you `{ mutate, loading, error }`.
 *   const { mutate, loading } = useApiMutation('post', '/ultra-admin/data-health/send-digest');
 *   const onClick = () => mutate({ body: { ... }, onSuccess: (data) => toast.success(...) });
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { apiClient } from '../lib/apiClient';

const DEFAULT_ERROR_MESSAGE = 'Bir şeyler ters gitti. Lütfen tekrar deneyin.';

/** Pull a sensible error string out of an axios error. */
function extractErrorMessage(err, fallback = DEFAULT_ERROR_MESSAGE) {
  if (!err) return fallback;
  const data = err.response?.data;
  if (typeof data === 'string') return data;
  if (data?.detail && typeof data.detail === 'string') return data.detail;
  if (data?.message && typeof data.message === 'string') return data.message;
  if (err.message) return err.message;
  return fallback;
}

/**
 * `useApi(path, options?)` — fetch on mount + on `refetch()`.
 *
 * @param {string|null} path — request path (relative to /api). Pass `null` to
 *        skip the request entirely (handy for "wait until tenantId is known").
 * @param {object} options
 *        - params:        axios `params` (query string).
 *        - method:        defaults to 'get'.
 *        - body:          request body for non-GET methods.
 *        - skip:          if true, the hook makes no request.
 *        - showErrorToast: defaults to true; pass false to handle errors yourself.
 *        - errorMessage:  override the default toast text.
 */
export function useApi(path, options = {}) {
  const {
    params, method = 'get', body, skip = false,
    showErrorToast = true, errorMessage,
  } = options;

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(!skip && Boolean(path));
  const [error,   setError]   = useState(null);

  // Guard against state updates after unmount.
  //
  // Subtlety: under React.StrictMode (dev-only) the component mounts → unmounts →
  // remounts in quick succession, but `useRef` is preserved across that cycle.
  // If we only ever flip `aliveRef.current = false` in the cleanup, the second
  // mount inherits a `false` flag and silently drops its own response, leaving
  // the UI stuck on "Loading…". We therefore *re-arm* the ref at the top of
  // every `run()` call. On a real unmount the cleanup still fires after the
  // request settles, so the original guarantee (no setState-after-unmount) holds.
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  // Stringify params so the dependency array is stable across re-renders.
  const paramsKey = params ? JSON.stringify(params) : '';
  const bodyKey   = body   ? JSON.stringify(body)   : '';

  const run = useCallback(async () => {
    if (skip || !path) {
      setLoading(false);
      return;
    }
    aliveRef.current = true; // re-arm — StrictMode-safe, see comment above.
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.request({ url: path, method, params, data: body });
      if (aliveRef.current) setData(res.data);
    } catch (err) {
      if (!aliveRef.current) return;
      setError(err);
      if (showErrorToast) {
        toast.error(extractErrorMessage(err, errorMessage));
      }
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [path, method, paramsKey, bodyKey, skip, showErrorToast, errorMessage]); // eslint-disable-line

  useEffect(() => { run(); }, [run]);

  return { data, loading, error, refetch: run };
}

/**
 * `useApiMutation('post', '/path')` — for actions triggered by user gestures
 * (button click, form submit). Returns `{ mutate, loading, error, data }`.
 *
 * `mutate(opts)` accepts:
 *   - body:        request body / form payload
 *   - params:      query-string params (for DELETE / PUT etc.)
 *   - onSuccess:   callback(data) — fires when the request resolves.
 *   - onError:     callback(err) — overrides default toast if set.
 *   - errorMessage: override default toast text.
 *   - showErrorToast: default true.
 */
export function useApiMutation(method, path) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const mutate = useCallback(async (opts = {}) => {
    const {
      body, params,
      onSuccess, onError,
      showErrorToast = true, errorMessage,
    } = opts;

    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.request({ url: path, method, data: body, params });
      setData(res.data);
      if (onSuccess) onSuccess(res.data);
      return res.data;
    } catch (err) {
      setError(err);
      if (onError) {
        onError(err);
      } else if (showErrorToast) {
        toast.error(extractErrorMessage(err, errorMessage));
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [method, path]);

  return { mutate, loading, error, data };
}
